import React from "react";
import { Box, Typography } from "@mui/material";

import useAssessmentStore from "components/Assessment/assessmentStore";

/**
 * Whole-attempt countdown (mobile) — same logic as the desktop
 * `Assessment/TotalTimer`, restyled for the phone header.
 *
 * The deadline is `now + time` minutes (`time` = totalTime, the summed
 * allottedTime of every question). While `isTimePaused` the deadline is pushed
 * forward 1s per tick, which is how desktop "pauses" without stopping the
 * interval. Every tick publishes `remainingInterviewTime`, which is what the
 * "Attention!" fullscreen dialog counts down. On expiry → endAssessment(true),
 * flagging the attempt as a timeout.
 *
 * @param {number} time  total minutes for the attempt
 */
export default function TotalTimer({ time }) {
  const endAssessment = useAssessmentStore((state) => state.endAssessment);
  const setTimeInterval = useAssessmentStore((state) => state.setTimeInterval);
  const isTimePaused = useAssessmentStore((state) => state.isTimePaused);
  const setRemainingInterviewTime = useAssessmentStore(
    (state) => state.setRemainingInterviewTime
  );

  const pauseTimeRef = React.useRef(false);
  const timerRef = React.useRef(null);
  // Held in a ref so the interval always reads the latest without re-subscribing.
  const endAssessmentRef = React.useRef(endAssessment);
  const setRemainingRef = React.useRef(setRemainingInterviewTime);

  const [timeSlices, setTimeSlices] = React.useState([
    "0", "0", ":", "0", "0", ":", "0", "0",
  ]);

  React.useEffect(() => {
    endAssessmentRef.current = endAssessment;
    setRemainingRef.current = setRemainingInterviewTime;
  }, [endAssessment, setRemainingInterviewTime]);

  React.useEffect(() => {
    pauseTimeRef.current = isTimePaused;
  }, [isTimePaused]);

  React.useEffect(() => {
    const deadline = new Date(Date.now() + time * 60 * 1000);

    const tick = () => {
      if (pauseTimeRef.current) deadline.setTime(deadline.getTime() + 1000);

      const total = Date.parse(deadline) - Date.parse(new Date());
      setRemainingRef.current(total);

      if (total < 0) {
        endAssessmentRef.current(true);
        return;
      }

      const seconds = Math.floor((total / 1000) % 60);
      const minutes = Math.floor((total / 1000 / 60) % 60);
      const hours = Math.floor((total / 1000 / 60 / 60) % 24);

      setTimeSlices([
        ...(hours > 9 ? hours.toString().split("") : ["0", hours]),
        ":",
        ...(minutes > 9 ? minutes.toString().split("") : ["0", minutes]),
        ":",
        ...(seconds > 9 ? seconds.toString().split("") : ["0", seconds]),
      ]);
    };

    if (timerRef.current) clearInterval(timerRef.current);
    const id = setInterval(tick, 1000);
    timerRef.current = id;
    setTimeInterval(id);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [time]);

  // Desktop hides the hours pair while it reads 00.
  const showHours = !(timeSlices[0] === "0" && timeSlices[1] === 0);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Typography variant="body01-medium" color="neutral.clr-900">
        Time Remaining
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {showHours && (
          <>
            <TimeDigit digit={`${timeSlices[0]}${timeSlices[1]}`} />
            <Separator />
          </>
        )}
        <TimeDigit digit={`${timeSlices[3]}${timeSlices[4]}`} />
        <Separator />
        <TimeDigit digit={`${timeSlices[6]}${timeSlices[7]}`} />
      </Box>
    </Box>
  );
}

function Separator() {
  return (
    <Typography
      component="span"
      color="primary.clr-300"
      sx={{ fontWeight: 800, px: 1 }}
    >
      :
    </Typography>
  );
}

function TimeDigit({ digit }) {
  return (
    <Box
      component="span"
      sx={{
        px: 2,
        py: 0.5,
        borderRadius: 1,
        border: "1px solid",
        borderColor: "neutral.clr-200",
        bgcolor: "shades.clr-white-900",
        color: "shades.clr-black-900",
      }}
    >
      <Typography variant="body01-semiBold">{digit}</Typography>
    </Box>
  );
}
